import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Phone, ArrowRight, Shield, AlertCircle } from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate mobile number
    if (mobile.length !== 10 || !/^\d+$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      setLoading(false);
      return;
    }

    try {
      // Check if mobile number exists in database
      const checkResponse = await axios.get(`${BACKEND_URL}/api/parents/check/${mobile}`);
      
      if (!checkResponse.data.exists) {
        setError('Mobile number not registered. Please contact school office.');
        toast.error('Not Registered', {
          description: 'Please contact school office to register'
        });
        setLoading(false);
        return;
      }

      // Mobile exists, send real OTP via Twilio
      const otpResponse = await axios.post(`${BACKEND_URL}/api/otp/send-otp`, {
        mobile_number: mobile
      });

      if (otpResponse.data.status === 'success') {
        // Store student data for later use
        sessionStorage.setItem('studentData', JSON.stringify(checkResponse.data.student));
        
        // Store OTP for display on next page
        sessionStorage.setItem('demo_otp', otpResponse.data.otp);

        toast.success('OTP Generated', {
          description: `Please check the verification page for your OTP`
        });

        setTimeout(() => {
          setLoading(false);
          navigate('/verify-otp', { state: { mobile } });
        }, 1000);
      } else {
        throw new Error('Failed to generate OTP');
      }

    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
      toast.error('Error', {
        description: errorMsg
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4A90E2]/10 via-white to-[#4A90E2]/5 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* School Logo */}
        <div className="text-center mb-8">
          <img 
            src={schoolInfo.logo} 
            alt="AMTEE PUBLIC SCHOOL" 
            className="h-24 w-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold text-gray-900">AMTEE PUBLIC SCHOOL</h1>
          <p className="text-gray-600">Parent Login Portal</p>
        </div>

        <Card className="shadow-2xl border-0 rounded-2xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
            <CardDescription>
              Enter your registered mobile number to receive OTP
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <Label htmlFor="mobile" className="text-base">Mobile Number</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="mobile"
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter mobile number"
                    className="pl-10 py-6 text-lg"
                    maxLength="10"
                    autoComplete="tel"
                    name="mobile"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter 10-digit mobile number (no need to add +91)
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#4A90E2] hover:bg-[#357ABD] py-6 text-lg rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={loading}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Secure Login</p>
                  <p className="text-xs text-gray-600 mt-1">
                    We'll send a 4-digit OTP to verify your identity. Valid for 5 minutes.
                  </p>
                </div>
              </div>
            </div>

            {/* Demo credentials - removed since we're using real database */}
            <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Not Registered?</p>
                  <p className="text-xs text-gray-600">
                    If your mobile number is not registered, please contact the school office for registration.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button
            variant="link"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-[#4A90E2]"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
